import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ListJobsView } from './pages/jobs/ListJobsView';
import { KanbanJobsView } from './pages/jobs/KanbanJobsView';
import { PrototypeSelector } from './pages/PrototypeSelector';
import { ProfilePage } from './pages/ProfilePage';
import { FirmManagementPage } from './pages/admin/FirmManagementPage';
import { OpsLanding } from './pages/ops/OpsLanding';
import { OpsDashboard } from './pages/ops/OpsDashboard';
import { OpsJobsPage } from './pages/ops/OpsJobsPage';
import { OpsAuditLogsPage } from './pages/ops/OpsAuditLogsPage';
import { OpsBinderVerificationPage } from './pages/ops/OpsBinderVerificationPage';
import { OpsTest } from './components/ops/OpsTest';
import { LoginPage } from './pages/auth/LoginPage';
import { NewPasswordPage } from './pages/auth/NewPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { OpsLayoutRoute } from './components/ops/OpsLayoutRoute';
import { ApiProvider } from './providers/ApiProvider';
import { AuthProvider } from './contexts/AuthContext';
import { FirmProvider } from './contexts/FirmContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { DefaultRedirect } from './components/common/DefaultRedirect';
import { useAuth } from "./contexts/AuthContext";
import { USER_ROLES } from "./utils/userPermissions";
import { useSSOHandler } from './hooks/useSSOHandler';
import theme from './theme';
import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function AppContent() {
  // Always running at the top level to catch SSO redirects
  useSSOHandler();

  const { user } = useAuth();
  const isAdmin = user?.role === USER_ROLES.admin


  return (
    <Routes>
      {/* Public Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/new-password" element={<NewPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Protected Routes with AppHeader */}
      <Route element={<ProtectedRoute />}>
        {/* Default redirect route */}
        <Route path="/" element={<DefaultRedirect />} />
        
        {/* Main app pages */}
        <Route path="/jobs/:jobId?" element={<ListJobsView />} />
        <Route path="/kanban" element={<KanbanJobsView />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/prototypes" element={<PrototypeSelector />} />
        
        {/* Ops Routes - nested under ProtectedRoute */}
        <Route path="/ops" element={<OpsLayoutRoute />}>
          <Route index element={<OpsLanding isAdmin={isAdmin} />} />
          <Route path="dash" element={<OpsDashboard />} />
          <Route path="jobs" element={<OpsJobsPage />} />
          <Route path="firms" element={<FirmManagementPage />} />
          <Route path="audit-logs" element={<OpsAuditLogsPage />} />
          <Route path="binder-verification" element={<OpsBinderVerificationPage />} />
          <Route path="test" element={<OpsTest />} />
        </Route>
      </Route>
      
      {/* Catch-all route - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ApiProvider>
              <FirmProvider>
                <ThemeProvider theme={theme}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <CssBaseline />
                    <AppContent />
                  </LocalizationProvider>
                </ThemeProvider>
              </FirmProvider>
              <ReactQueryDevtools initialIsOpen={false} />
            </ApiProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
